import {useMemo, useState} from 'react';
import type {ChangeEvent, ReactElement} from 'react';
import {Button, Size as ButtonSize, Type as ButtonType} from '@Components/button/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {AiAgentMode} from '@/ai-chat/providers/ai-chat-provider.types';
import {AgentModePill} from '@/ai-chat/components/agent-mode-pill/agent-mode-pill';
import {DecisionsAssist} from '@Components/ai-chat-window/ai-chat-window.types';
import {SuperUserOptionsGroup} from './components/super-user-options-group/super-user-options-group';
import {SuperUserOverrideSelect} from './components/super-user-override-select/super-user-override-select';
import {SuperUserPromptVariants} from './components/super-user-prompt-variants/super-user-prompt-variants';
import {SuperUserToggleRow} from './components/super-user-toggle-row/super-user-toggle-row';
import {WEIGHTED_PICK_VALUE} from './components/super-user-prompt-variants/super-user-prompt-variants.types';
import type {
  AgentChangeHandler,
  AgentOverrideRow,
  DownloadAttachmentsButtonProps,
  ForcedImageProvider,
  ForcedTextExtractionModel,
  ForcedTextRemovalMethod,
  MockCredits,
  SuperUserOptionsPopupProps,
} from './ai-chat-super-user-options.types';
import {POPUP_HEIGHT_UNCAPPED_PX, SUPER_USER_COPY} from './ai-chat-super-user-options.types';
import {
  asUngroupedSection,
  clearSuperUserVariantOverrides,
  createOverrideSelectHandler,
  downloadUserAttachments,
  getForcedImageProviderOptions,
  getForcedTextExtractionModelSections,
  getForcedTextRemovalMethodSections,
  getInitialAgents,
  getInitialForcedImageProvider,
  getInitialForcedTextExtractionModel,
  getInitialForcedTextRemovalMethod,
  getInitialMockCredits,
  getMockCreditsOptions,
  isForcedImageProvider,
  isForcedTextExtractionModel,
  isForcedTextRemovalMethod,
  isMockCredits,
  setSuperUserForcedImageProvider,
  setSuperUserForcedTextExtractionModel,
  setSuperUserForcedTextRemovalMethod,
  setSuperUserMockCredits,
  setSuperUserVariantOverride,
} from './ai-chat-super-user-options.library';
import styles from './ai-chat-super-user-options.module.scss';

export function AiChatSuperUserOptions(): ReactElement | null {
  const {state, actions} = useAiChat();
  const initialAgents = useMemo<AgentOverrideRow[]>(() => getInitialAgents(), []);
  const [agents, setAgents] = useState<AgentOverrideRow[]>(initialAgents);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingAttachments, setIsDownloadingAttachments] = useState<boolean>(false);
  const [forcedProvider, setForcedProvider] = useState<ForcedImageProvider>(() => getInitialForcedImageProvider());
  const [forcedTextRemovalMethod, setForcedTextRemovalMethod] = useState<ForcedTextRemovalMethod>(() => getInitialForcedTextRemovalMethod());
  const [mockCredits, setMockCredits] = useState<MockCredits>(() => getInitialMockCredits());
  const [forcedTextExtractionModel, setForcedTextExtractionModel] = useState<ForcedTextExtractionModel>(() => getInitialForcedTextExtractionModel());

  if (!state.isSuperUser) {
    return null;
  }

  const handleChange: AgentChangeHandler =
    (agentType: string) =>
    (event: ChangeEvent<HTMLSelectElement>): void => {
      const value = event.target.value;
      void setSuperUserVariantOverride(agentType, value).then(
        () => {
          setError(null);
          setAgents((current) =>
            current.map((row) => (row.agentType === agentType ? {...row, currentAlias: value === WEIGHTED_PICK_VALUE ? null : value} : row))
          );
        },
        () => {
          setError(window.i18next.t('pmwjs_variant_save_failed'));
        }
      );
    };

  const handleClearAll = (): void => {
    void clearSuperUserVariantOverrides().then(
      () => {
        setError(null);
        setAgents((current) => current.map((row) => ({...row, currentAlias: null})));
      },
      () => {
        setError(window.i18next.t('pmwjs_variant_save_failed'));
      }
    );
  };

  const handleShowDeletedChange = (): void => {
    actions.setShowDeletedMessages(!state.showDeletedMessages);
  };

  const handleShowGenerationContextChange = (): void => {
    actions.setHideGenerationContext(!state.hideGenerationContext);
  };

  const handleAgentModeChange = (): void => {
    const nextMode = state.mode === AiAgentMode.AGENT ? AiAgentMode.CHAT : AiAgentMode.AGENT;
    actions.setMode(nextMode);
  };

  const handleDecisionsAssistChange = (): void => {
    if (state.decisionsAssist === DecisionsAssist.ON) {
      actions.setDecisionsAssist(DecisionsAssist.OFF);
      return;
    }

    actions.setDecisionsAssist(DecisionsAssist.ON);
  };

  const handleForcedProviderChange = createOverrideSelectHandler<ForcedImageProvider>({
    isValidValue: isForcedImageProvider,
    save: setSuperUserForcedImageProvider,
    onSaved: (value: ForcedImageProvider): void => {
      setError(null);
      setForcedProvider(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_variant_save_failed'));
    },
  });

  const handleForcedTextRemovalMethodChange = createOverrideSelectHandler<ForcedTextRemovalMethod>({
    isValidValue: isForcedTextRemovalMethod,
    save: setSuperUserForcedTextRemovalMethod,
    onSaved: (value: ForcedTextRemovalMethod): void => {
      setError(null);
      setForcedTextRemovalMethod(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_ai_chat_force_text_removal_method_save_failed'));
    },
  });

  const handleMockCreditsChange = createOverrideSelectHandler<MockCredits>({
    isValidValue: isMockCredits,
    save: setSuperUserMockCredits,
    onSaved: (value: MockCredits): void => {
      setError(null);
      setMockCredits(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_variant_save_failed'));
    },
  });

  const handleForcedTextExtractionModelChange = createOverrideSelectHandler<ForcedTextExtractionModel>({
    isValidValue: isForcedTextExtractionModel,
    save: setSuperUserForcedTextExtractionModel,
    onSaved: (value: ForcedTextExtractionModel): void => {
      setError(null);
      setForcedTextExtractionModel(value);
    },
    onError: (): void => {
      setError(window.i18next.t('pmwjs_ai_chat_force_text_extraction_model_save_failed'));
    },
  });

  const handleDownloadAttachments = (): void => {
    const chatId = state.currentChatId;
    if (chatId === undefined || isDownloadingAttachments) {
      return;
    }
    setIsDownloadingAttachments(true);
    setError(null);
    downloadUserAttachments(chatId)
      .catch((downloadError: unknown) => {
        const message = downloadError instanceof Error ? downloadError.message : window.i18next.t('pmwjs_super_user_attachments_download_failed');
        setError(message);
      })
      .finally(() => {
        setIsDownloadingAttachments(false);
      });
  };

  return (
    <Dropdown
      id="ai-chat-super-user-options"
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popUpHasCustomWidth
      maxHeight={POPUP_HEIGHT_UNCAPPED_PX}
      popupClassName={styles.popup}
      doCloseOnInnerClick={false}
      selector={renderSelector()}
      popup={renderPopup({
        isAgentMode: state.mode === AiAgentMode.AGENT,
        onAgentModeChange: handleAgentModeChange,
        decisionsAssist: state.decisionsAssist,
        onDecisionsAssistChange: handleDecisionsAssistChange,
        showDeleted: state.showDeletedMessages,
        onShowDeletedChange: handleShowDeletedChange,
        showGenerationContext: !state.hideGenerationContext,
        onShowGenerationContextChange: handleShowGenerationContextChange,
        currentChatId: state.currentChatId,
        isDownloadingAttachments,
        onDownloadAttachments: handleDownloadAttachments,
        forcedProvider,
        onForcedProviderChange: handleForcedProviderChange,
        forcedTextRemovalMethod,
        onForcedTextRemovalMethodChange: handleForcedTextRemovalMethodChange,
        mockCredits,
        onMockCreditsChange: handleMockCreditsChange,
        forcedTextExtractionModel,
        onForcedTextExtractionModelChange: handleForcedTextExtractionModelChange,
        agents,
        onChangeAgent: handleChange,
        onClearAll: handleClearAll,
        error,
      })}
    />
  );
}

function renderSelector(): ReactElement {
  return (
    <div className={styles.selector}>
      <AgentModePill />
      <Icon
        icon="icon-tools"
        shape={IconShape.SQUARE}
        type={IconType.GHOST_UNCOLORED}
        size={IconSize.SIZE_ICON_16}
        isUsedAsButton
        ariaLabel={SUPER_USER_COPY.title}
      />
    </div>
  );
}

function renderPopup(props: SuperUserOptionsPopupProps): ReactElement {
  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <Text val={SUPER_USER_COPY.title} size={TextSize.MD} bold className={styles.title} />
      </div>
      {renderError(props.error)}
      <div className={styles.groups}>
        <SuperUserOptionsGroup title={SUPER_USER_COPY.turnSection}>
          <SuperUserToggleRow id="ai-chat-agent-mode" label={window.i18next.t('pmwjs_ai_chat_agent_mode')} isChecked={props.isAgentMode} onChange={props.onAgentModeChange} />
          {renderDecisionsAssistRow(props)}
        </SuperUserOptionsGroup>
        <SuperUserOptionsGroup title={SUPER_USER_COPY.displaySection}>
          <SuperUserToggleRow
            id="ai-chat-show-deleted-messages"
            label={window.i18next.t('pmwjs_show_deleted_messages')}
            isChecked={props.showDeleted}
            onChange={props.onShowDeletedChange}
          />
          <SuperUserToggleRow
            id="ai-chat-show-generation-context"
            label={window.i18next.t('pmwjs_show_generation_context')}
            isChecked={props.showGenerationContext}
            onChange={props.onShowGenerationContextChange}
          />
        </SuperUserOptionsGroup>
        {renderModelOverrides(props)}
        {renderPromptVariants(props)}
        <SuperUserOptionsGroup title={SUPER_USER_COPY.actionsSection}>
          {renderDownloadAttachmentsButton({
            currentChatId: props.currentChatId,
            isDownloading: props.isDownloadingAttachments,
            onDownload: props.onDownloadAttachments,
          })}
        </SuperUserOptionsGroup>
      </div>
    </div>
  );
}

function renderDecisionsAssistRow(props: SuperUserOptionsPopupProps): ReactElement | null {
  if (!props.isAgentMode) {
    return null;
  }

  return (
    <SuperUserToggleRow
      id="ai-chat-decisions-assist"
      label={SUPER_USER_COPY.decisionsAssist}
      hint={SUPER_USER_COPY.decisionsAssistHint}
      isChecked={props.decisionsAssist === DecisionsAssist.ON}
      onChange={props.onDecisionsAssistChange}
    />
  );
}

function renderModelOverrides(props: SuperUserOptionsPopupProps): ReactElement {
  return (
    <SuperUserOptionsGroup title={SUPER_USER_COPY.modelOverridesSection}>
      <SuperUserOverrideSelect
        id="ai-chat-force-image-provider"
        label={window.i18next.t('pmwjs_ai_chat_force_image_provider')}
        value={props.forcedProvider}
        sections={asUngroupedSection(getForcedImageProviderOptions())}
        onChange={props.onForcedProviderChange}
      />
      <SuperUserOverrideSelect
        id="ai-chat-force-text-removal-method"
        label={window.i18next.t('pmwjs_ai_chat_force_text_removal_method')}
        value={props.forcedTextRemovalMethod}
        sections={getForcedTextRemovalMethodSections()}
        onChange={props.onForcedTextRemovalMethodChange}
      />
      <SuperUserOverrideSelect
        id="ai-chat-force-text-extraction-model"
        label={window.i18next.t('pmwjs_ai_chat_force_text_extraction_model')}
        value={props.forcedTextExtractionModel}
        sections={getForcedTextExtractionModelSections()}
        onChange={props.onForcedTextExtractionModelChange}
      />
      <SuperUserOverrideSelect
        id="ai-chat-mock-credits"
        label={window.i18next.t('pmwjs_ai_chat_mock_credits')}
        value={props.mockCredits}
        sections={asUngroupedSection(getMockCreditsOptions())}
        onChange={props.onMockCreditsChange}
      />
    </SuperUserOptionsGroup>
  );
}

function renderPromptVariants(props: SuperUserOptionsPopupProps): ReactElement | null {
  if (props.agents.length === 0) {
    return null;
  }

  return (
    <SuperUserOptionsGroup title={SUPER_USER_COPY.promptVariantsSection} headingAction={renderClearAllButton(props.onClearAll)}>
      <SuperUserPromptVariants agents={props.agents} onChangeAgent={props.onChangeAgent} />
    </SuperUserOptionsGroup>
  );
}

function renderClearAllButton(onClearAll: () => void): ReactElement {
  return <Button type={ButtonType.GHOST} size={ButtonSize.XSMALL} text={window.i18next.t('pmwjs_clear_all')} customClasses={styles.clearAllButton} onClick={onClearAll} />;
}

function renderDownloadAttachmentsButton(props: DownloadAttachmentsButtonProps): ReactElement {
  return (
    <Button
      type={ButtonType.GHOST}
      size={ButtonSize.SMALL}
      text={window.i18next.t('pmwjs_super_user_download_user_attachments')}
      icon="icon-download"
      disabled={props.currentChatId === undefined}
      isLoading={props.isDownloading}
      onClick={props.onDownload}
    />
  );
}

function renderError(error: string | null): ReactElement | null {
  if (error === null) {
    return null;
  }

  return (
    <div className={styles.error} role="alert">
      <Text val={error} size={TextSize.SM} />
    </div>
  );
}
